import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Users, Layers, UserPlus, Pencil, Trash2, ChevronRight, Upload, Mail, Send, BookOpen, Calendar, Bell, Key, GraduationCap, Activity, Shield } from "lucide-react";
import TeachersTab from "@/pages/admin/TeachersTab";
import StaffTab from "@/pages/admin/StaffTab";

const ConfirmButton = ({ onConfirm, label = "Fshi", testId }) => {
    const [armed, setArmed] = useState(false);
    return (
        <button type="button"
            onClick={() => { if (armed) { onConfirm(); setArmed(false); } else { setArmed(true); setTimeout(() => setArmed(false), 3000); } }}
            data-testid={testId}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition ${armed ? "bg-terracotta text-white" : "bg-terracotta/10 text-terracotta hover:bg-terracotta/20"}`}>
            <Trash2 className="w-3 h-3" /> {armed ? "Konfirmo" : label}
        </button>
    );
};

const ImportDialog = ({ kind, label, onDone }) => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        if (!file) return;
        setBusy(true); setResult(null);
        try {
            const fd = new FormData(); fd.append("file", file);
            const r = await api.post(`/admin/import/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(r.data); onDone && onDone();
        } catch (e2) { setResult({ error: formatApiError(e2.response?.data?.detail) || e2.message }); }
        finally { setBusy(false); }
    };
    const example = {
        students: "class_name,student_name,parent_email,parent_name,year",
        classes: "name,year",
        comments: "student_name,subject_name,category,text,date",
        absences: "student_name,subject_name,date,hours,comment",
    }[kind];
    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFile(null); setResult(null); } }}>
            <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid={`import-${kind}-btn`}>
                    <Upload className="w-4 h-4" /> Import {label}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Import CSV — {label}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="text-xs text-slate-600">
                        <div className="font-bold uppercase tracking-widest text-slate-500 mb-1">Kolonat e pritura:</div>
                        <code className="block bg-slate-100 p-2 rounded text-xs font-mono">{example}</code>
                    </div>
                    <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files[0])} required data-testid={`import-${kind}-file`} />
                    {result && (
                        <div className="text-xs bg-bone/60 border border-slate-200 rounded-lg p-3" data-testid={`import-${kind}-result`}>
                            {result.error ? <div className="text-terracotta font-bold">{result.error}</div> : (
                                <>
                                    <div><strong className="text-sage">{result.inserted}</strong> të shtuara nga {result.total} • <strong className="text-terracotta">{result.skipped?.length || 0}</strong> të kapërcyera</div>
                                    {result.skipped && result.skipped.length > 0 && (
                                        <details className="mt-2"><summary className="cursor-pointer">Detaje</summary>
                                            <pre className="text-[10px] mt-1 max-h-32 overflow-auto">{JSON.stringify(result.skipped.slice(0, 5), null, 2)}</pre>
                                        </details>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" disabled={busy || !file} className="bg-oxford hover:bg-oxfordhover text-white" data-testid={`import-${kind}-submit`}>{busy ? "Duke ngarkuar…" : "Ngarko CSV"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default function AdminDashboard() {
    const [tab, setTab] = useState("students");
    const [classes, setClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [parents, setParents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [filterClass, setFilterClass] = useState("all");
    const [loading, setLoading] = useState(true);
    const [emailStatus, setEmailStatus] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const [c, s, p, em, su] = await Promise.all([
                api.get("/classes"),
                api.get("/teacher/students", { params: filterClass !== "all" ? { class_id: filterClass } : {} }),
                api.get("/admin/parents"),
                api.get("/admin/email-status").catch(() => ({ data: { configured: false } })),
                api.get("/subjects"),
            ]);
            setClasses(c.data); setStudents(s.data); setParents(p.data); setEmailStatus(em.data); setSubjects(su.data);
        } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterClass]);

    const classMap = Object.fromEntries(classes.map((c) => [c.id, c]));

    return (
        <Layout>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Paneli i Super Adminit</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Menaxhimi i Shkollës</h1>
                <p className="mt-2 text-slate-600 text-sm sm:text-base">German Gymnasium • 12 klasa (10–12)</p>
            </div>

            {emailStatus && (
                <div className={`mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-bold ${emailStatus.configured ? "bg-sage/15 border-sage/30 text-sage" : "bg-slate-100 border-slate-200 text-slate-600"}`} data-testid="email-status-pill">
                    <Mail className="w-3.5 h-3.5" /> Email: {emailStatus.configured ? "i lidhur (Resend)" : "i pa-konfiguruar (mocked)"}
                    {emailStatus.admin_cc && <span className="font-mono">• CC {emailStatus.admin_cc}</span>}
                </div>
            )}

            {/* Quick-access tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="admin-quick-tiles">
                <Link to="/admin/analytics" className="bg-gradient-to-br from-oxford to-oxfordhover text-white rounded-xl p-4 flex flex-col items-start gap-2 transition hover:shadow-lg col-span-2 sm:col-span-1" data-testid="admin-tile-analytics">
                    <div className="w-9 h-9 rounded-lg bg-white/20 text-white flex items-center justify-center"><Activity className="w-5 h-5" /></div>
                    <div className="text-sm font-bold">Analitikat</div>
                    <div className="text-[10px] text-white/70 -mt-1">Përdorimi i platformës</div>
                </Link>
                <Link to="/admin/timetable" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-4 flex flex-col items-start gap-2 transition" data-testid="admin-tile-timetable">
                    <div className="w-9 h-9 rounded-lg bg-oxford/10 text-oxford flex items-center justify-center"><Calendar className="w-5 h-5" /></div>
                    <div className="text-sm font-bold text-slate-900">Orari</div>
                </Link>
                <Link to="/admin/notify" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-4 flex flex-col items-start gap-2 transition" data-testid="admin-tile-notify">
                    <div className="w-9 h-9 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center"><Bell className="w-5 h-5" /></div>
                    <div className="text-sm font-bold text-slate-900">Njoftime</div>
                </Link>
                <Link to="/teacher/lms" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-4 flex flex-col items-start gap-2 transition" data-testid="admin-tile-lms">
                    <div className="w-9 h-9 rounded-lg bg-sage/15 text-sage flex items-center justify-center"><BookOpen className="w-5 h-5" /></div>
                    <div className="text-sm font-bold text-slate-900">LMS</div>
                </Link>
                <Link to="/inbox" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-4 flex flex-col items-start gap-2 transition" data-testid="admin-tile-inbox">
                    <div className="w-9 h-9 rounded-lg bg-seablue/10 text-seablue flex items-center justify-center"><Bell className="w-5 h-5" /></div>
                    <div className="text-sm font-bold text-slate-900">Inbox</div>
                </Link>
            </div>


            <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="admin-tabs">
                {[
                    { k: "students", l: "Nxënësit", i: <Users className="w-4 h-4" /> },
                    { k: "classes", l: "Klasat", i: <Layers className="w-4 h-4" /> },
                    { k: "subjects", l: "Lëndët", i: <BookOpen className="w-4 h-4" /> },
                    { k: "teachers", l: "Mësuesit", i: <GraduationCap className="w-4 h-4" /> },
                    { k: "staff", l: "Stafi", i: <Shield className="w-4 h-4" /> },
                    { k: "parents", l: "Prindërit", i: <UserPlus className="w-4 h-4" /> },
                ].map((t) => (
                    <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition whitespace-nowrap ${tab === t.k ? "bg-oxford text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-oxford"}`}>
                        {t.i} {t.l}
                    </button>
                ))}
            </div>

            {tab === "students" && <StudentsTab classes={classes} classMap={classMap} students={students} loading={loading} filterClass={filterClass} setFilterClass={setFilterClass} refresh={refresh} />}
            {tab === "classes" && <ClassesTab classes={classes} loading={loading} refresh={refresh} />}
            {tab === "subjects" && <SubjectsTab subjects={subjects} loading={loading} refresh={refresh} />}
            {tab === "teachers" && <TeachersTab />}
            {tab === "staff" && <StaffTab />}
            {tab === "parents" && <ParentsTab parents={parents} loading={loading} refresh={refresh} />}
        </Layout>
    );
}

const StudentsTab = ({ classes, classMap, students, loading, filterClass, setFilterClass, refresh }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", parent_email: "", year: 10, class_id: "" });
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", year: 10, notify_email: "", auto_monthly_report: true });

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            await api.post("/students", { ...form, class_id: form.class_id || null });
            setForm({ name: "", parent_email: "", year: 10, class_id: "" }); setOpen(false); refresh();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    const submitEdit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.patch(`/students/${edit.id}`, editForm);
            setEdit(null); refresh();
        } finally { setBusy(false); }
    };
    const del = async (sid) => { await api.delete(`/students/${sid}`); refresh(); };
    const assignClass = async (sid, cid) => { await api.post("/classes/assign", { student_id: sid, class_id: cid || null }); refresh(); };
    const sendReport = async (sid) => {
        try {
            const r = await api.post("/admin/send-monthly-report", { student_id: sid });
            alert(r.data.ok ? `✓ Raporti u dërgua te ${r.data.to}` : `✗ ${JSON.stringify(r.data.result)}`);
        } catch (e) { alert("Gabim: " + (e.response?.data?.detail || e.message)); }
    };
    const [loginDlg, setLoginDlg] = useState(null); // student object
    const [loginForm, setLoginForm] = useState({ email: "", password: "student123" });
    const [loginBusy, setLoginBusy] = useState(false);
    const [loginErr, setLoginErr] = useState("");
    const submitLogin = async (e) => {
        e.preventDefault(); setLoginErr(""); setLoginBusy(true);
        try {
            await api.post("/admin/students/login", { student_id: loginDlg.id, ...loginForm });
            setLoginDlg(null); setLoginForm({ email: "", password: "student123" }); refresh();
            alert(`Llogaria e nxënësit u krijua. Email: ${loginForm.email}`);
        } catch (e2) { setLoginErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setLoginBusy(false); }
    };

    // Change password for student's user account
    const [pwdDlg, setPwdDlg] = useState(null);
    const [pwdInput, setPwdInput] = useState("");
    const submitPwd = async (e) => {
        e.preventDefault();
        if (!pwdDlg?.user_id) { alert("Ky nxënës nuk ka llogari të krijuar ende."); return; }
        try {
            await api.post(`/admin/users/${pwdDlg.user_id}/password`, { new_password: pwdInput });
            setPwdDlg(null); setPwdInput("");
            alert("Fjalëkalimi u ndryshua.");
        } catch (e2) { alert("Gabim: " + (e2.response?.data?.detail || e2.message)); }
    };

    return (
        <>
            <div className="flex flex-wrap gap-2 mb-4 items-center">
                <button onClick={() => setFilterClass("all")} className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterClass === "all" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`} data-testid="admin-filter-all">Të gjithë ({students.length})</button>
                {classes.map((c) => (
                    <button key={c.id} onClick={() => setFilterClass(c.id)} className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterClass === c.id ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`} data-testid={`admin-filter-${c.id}`}>{c.name} • {c.student_count}</button>
                ))}
                <button onClick={() => setFilterClass("unassigned")} className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterClass === "unassigned" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`}>Pa klasë</button>
                <div className="ml-auto flex flex-wrap gap-2">
                    <ImportDialog kind="students" label="Nxënës" onDone={refresh} />
                    <ImportDialog kind="comments" label="Komente" onDone={refresh} />
                    <ImportDialog kind="absences" label="Mungesa" onDone={refresh} />
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button size="sm" className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="admin-add-student-btn"><Plus className="w-4 h-4" /> Shto Nxënës</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-heading">Shto Nxënës</DialogTitle></DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <Input placeholder="Emri i nxënësit" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-add-student-name" />
                                <Input type="email" placeholder="Emaili i prindit" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} required data-testid="admin-add-student-email" />
                                <Input type="number" min={10} max={12} value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || 10 })} data-testid="admin-add-student-year" />
                                <select value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="admin-add-student-class">
                                    <option value="">— Pa klasë —</option>
                                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                {err && <div className="text-xs text-terracotta">{err}</div>}
                                <DialogFooter>
                                    <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-add-student-submit">{busy ? "Duke ruajtur…" : "Ruaj"}</Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {loading ? <div className="p-6 text-slate-500">Duke ngarkuar…</div> : students.length === 0 ? <div className="p-8 text-center text-slate-500">Asnjë nxënës.</div> : (
                    <div className="divide-y divide-slate-200">
                        {students.map((s) => (
                            <div key={s.id} className="flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-3" data-testid={`admin-student-row-${s.id}`}>
                                <div className="w-10 h-10 rounded-xl bg-oxford text-white flex items-center justify-center font-bold text-sm shrink-0">{s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-900 truncate">{s.name}</div>
                                    <div className="text-[11px] text-slate-500 truncate">
                                        Viti {s.year} {s.class_id && classMap[s.class_id] ? `• ${classMap[s.class_id].name}` : "• Pa klasë"}
                                        {s.auto_monthly_report && <span className="ml-1 text-sage">• Auto raport</span>}
                                    </div>
                                </div>
                                <select value={s.class_id || ""} onChange={(e) => assignClass(s.id, e.target.value)} className="h-8 rounded-md border border-slate-300 bg-white text-xs px-2 hidden sm:block" data-testid={`admin-assign-${s.id}`}>
                                    <option value="">Pa klasë</option>
                                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                                <button onClick={() => sendReport(s.id)} className="p-1.5 rounded-md text-seablue hover:bg-seablue/10" title="Dërgo raport mujor" data-testid={`admin-send-report-${s.id}`}><Send className="w-4 h-4" /></button>
                                {s.user_id ? (
                                    <button onClick={() => { setPwdDlg(s); setPwdInput(""); }} className="p-1.5 rounded-md text-seablue hover:bg-seablue/10" title="Ndrysho fjalëkalimin" data-testid={`admin-pwd-${s.id}`}><Key className="w-4 h-4" /></button>
                                ) : (
                                    <button onClick={() => { setLoginDlg(s); setLoginForm({ email: s.notify_email || "", password: "student123" }); }} className="p-1.5 rounded-md text-sage hover:bg-sage/10" title="Krijo login nxënësi" data-testid={`admin-create-login-${s.id}`}><Key className="w-4 h-4" /></button>
                                )}
                                <button onClick={() => { setEdit(s); setEditForm({ name: s.name, year: s.year, notify_email: s.notify_email || "", auto_monthly_report: s.auto_monthly_report !== false }); }} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`admin-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                                <ConfirmButton onConfirm={() => del(s.id)} testId={`admin-delete-${s.id}`} />
                                <Link to={`/teacher/student/${s.id}`} className="text-slate-400 hover:text-oxford hidden sm:block"><ChevronRight className="w-5 h-5" /></Link>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Edito Nxënësin</DialogTitle></DialogHeader>
                    <form onSubmit={submitEdit} className="space-y-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Emri</label>
                            <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="admin-edit-name-input" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Viti</label>
                            <Input type="number" min={10} max={12} value={editForm.year} onChange={(e) => setEditForm({ ...editForm, year: parseInt(e.target.value) || 10 })} data-testid="admin-edit-year-input" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1">Email për raport (prind)</label>
                            <Input type="email" value={editForm.notify_email} onChange={(e) => setEditForm({ ...editForm, notify_email: e.target.value })} placeholder="prind@example.al" data-testid="admin-edit-notify-input" />
                        </div>
                        <div className="flex items-center justify-between p-3 bg-bone/40 rounded-lg border border-slate-200">
                            <div>
                                <div className="font-bold text-sm text-slate-800">Raport mujor automatik</div>
                                <div className="text-xs text-slate-500">Dërgohet datë 1 të çdo muaji</div>
                            </div>
                            <Switch checked={editForm.auto_monthly_report} onCheckedChange={(v) => setEditForm({ ...editForm, auto_monthly_report: v })} data-testid="admin-edit-auto-report" />
                        </div>
                        <DialogFooter>
                            <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-edit-submit">Ruaj</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!loginDlg} onOpenChange={(v) => !v && setLoginDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Krijo Login për {loginDlg?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitLogin} className="space-y-3">
                        <p className="text-xs text-slate-600">Krijon llogari të re me rolin "Nxënës" dhe e lidh me këtë nxënës.</p>
                        <Input type="email" placeholder="nxenesi@gg-ks.com" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required data-testid="student-login-email" />
                        <Input value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required data-testid="student-login-password" />
                        {loginErr && <div className="text-xs text-terracotta">{loginErr}</div>}
                        <DialogFooter><Button type="submit" disabled={loginBusy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="student-login-submit">{loginBusy ? "Duke krijuar…" : "Krijo Llogari"}</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!pwdDlg} onOpenChange={(v) => !v && setPwdDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Ndrysho fjalëkalimin për {pwdDlg?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitPwd} className="space-y-3">
                        <Input type="password" placeholder="Fjalëkalimi i ri (min 6)" value={pwdInput} onChange={(e) => setPwdInput(e.target.value)} required minLength={6} data-testid="student-pwd-input" />
                        <DialogFooter><Button type="submit" className="bg-oxford hover:bg-oxfordhover text-white" data-testid="student-pwd-submit">Ndrysho</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

        </>
    );
};

const ClassesTab = ({ classes, loading, refresh }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", year: 10, direction: "informatics" });
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(null);
    const [assignDlg, setAssignDlg] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [assignTid, setAssignTid] = useState("");
    useEffect(() => { (async () => { try { const r = await api.get("/admin/teachers"); setTeachers(r.data); } catch { /* ignore */ } })(); }, []);
    const submit = async (e) => { e.preventDefault(); setBusy(true); try { await api.post("/classes", form); setForm({ name: "", year: 10, direction: "informatics" }); setOpen(false); refresh(); } finally { setBusy(false); } };
    const submitEdit = async (e) => { e.preventDefault(); setBusy(true); try { await api.patch(`/classes/${edit.id}`, edit); setEdit(null); refresh(); } finally { setBusy(false); } };
    const submitAssign = async (e) => { e.preventDefault(); setBusy(true); try { await api.post("/admin/class-teacher", { class_id: assignDlg.id, teacher_id: assignTid }); setAssignDlg(null); setAssignTid(""); refresh(); } catch (e2) { alert("Gabim: " + (e2.response?.data?.detail || e2.message)); } finally { setBusy(false); } };
    const unassignKujdestari = async (cid) => { if (!window.confirm("Hiq kujdestarin?")) return; await api.delete(`/admin/class-teacher/${cid}`); refresh(); };
    const del = async (cid) => { await api.delete(`/classes/${cid}`); refresh(); };
    const dirLabel = (d) => d === "natural_sciences" ? "Shkenca Natyrore" : d === "informatics" ? "Teknologji Informative" : "Të gjitha";
    return (
        <>
            <div className="flex flex-wrap justify-end gap-2 mb-4">
                <ImportDialog kind="classes" label="Klasa" onDone={refresh} />
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button size="sm" className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="admin-add-class-btn"><Plus className="w-4 h-4" /> Shto Klasë</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading">Shto Klasë</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <Input placeholder="Emri (p.sh. 10-Mayer)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-add-class-name" />
                            <Input type="number" min={10} max={12} value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || 10 })} data-testid="admin-add-class-year" />
                            <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="admin-add-class-direction">
                                <option value="informatics">Teknologji Informative</option>
                                <option value="natural_sciences">Shkenca Natyrore</option>
                                <option value="all">Të gjitha</option>
                            </select>
                            <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-add-class-submit">Ruaj</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {loading ? <div>Duke ngarkuar…</div> : classes.map((c) => (
                    <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-2" data-testid={`admin-class-${c.id}`}>
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center"><Layers className="w-5 h-5" /></div>
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-bold text-slate-900">{c.name}</div>
                                <div className="text-xs text-slate-500">Viti {c.year} • {c.student_count} nxënës</div>
                                <div className="text-[10px] uppercase tracking-widest text-oxford font-bold mt-0.5">{dirLabel(c.direction)}</div>
                            </div>
                            <button onClick={() => setEdit({ ...c })} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`admin-edit-class-${c.id}`}><Pencil className="w-4 h-4" /></button>
                            <ConfirmButton onConfirm={() => del(c.id)} testId={`admin-delete-class-${c.id}`} />
                        </div>
                        <div className={`flex items-center gap-2 pt-2 border-t-2 ${c.class_teacher_id ? "border-sage/30" : "border-amber-300"}`}>
                            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Kujdestar:</span>
                            {c.class_teacher_id ? (
                                <>
                                    <span className="text-xs font-semibold text-oxford flex-1 truncate" data-testid={`class-kujdestari-${c.id}`}>{c.class_teacher_name}</span>
                                    <button onClick={() => { setAssignDlg(c); setAssignTid(c.class_teacher_id); }} className="text-[11px] text-seablue hover:underline font-semibold" data-testid={`change-kujdestari-${c.id}`}>Ndryshoj</button>
                                    <button onClick={() => unassignKujdestari(c.id)} className="text-[11px] text-terracotta hover:underline" data-testid={`unassign-kujdestari-${c.id}`}>Hiq</button>
                                </>
                            ) : (
                                <button onClick={() => { setAssignDlg(c); setAssignTid(""); }}
                                    className="flex-1 text-xs bg-amber-100 text-amber-900 font-bold rounded-md py-1.5 hover:bg-amber-200 transition border border-amber-300"
                                    data-testid={`assign-kujdestari-${c.id}`}>
                                    + Cakto Kujdestar
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Edito Klasën</DialogTitle></DialogHeader>
                    {edit && <form onSubmit={submitEdit} className="space-y-3">
                        <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} required data-testid="admin-edit-class-name" />
                        <Input type="number" min={10} max={12} value={edit.year} onChange={(e) => setEdit({ ...edit, year: parseInt(e.target.value) || 10 })} />
                        <select value={edit.direction || "all"} onChange={(e) => setEdit({ ...edit, direction: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                            <option value="informatics">Teknologji Informative</option>
                            <option value="natural_sciences">Shkenca Natyrore</option>
                            <option value="all">Të gjitha</option>
                        </select>
                        <DialogFooter><Button type="submit" className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-edit-class-submit">Ruaj</Button></DialogFooter>
                    </form>}
                </DialogContent>
            </Dialog>
            <Dialog open={!!assignDlg} onOpenChange={(v) => !v && setAssignDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Cakto kujdestar për {assignDlg?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitAssign} className="space-y-3">
                        <select required value={assignTid} onChange={(e) => setAssignTid(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-3" data-testid="kujdestari-form-teacher">
                            <option value="">— Zgjidh mësuesin —</option>
                            {teachers.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.email})</option>)}
                        </select>
                        <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="kujdestari-form-submit">Cakto</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

const SubjectsTab = ({ subjects, loading, refresh }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", type: "general", direction: "all" });
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(null);
    const [filterDir, setFilterDir] = useState("all");
    const submit = async (e) => { e.preventDefault(); setBusy(true); try { await api.post("/subjects", form); setForm({ name: "", type: "general", direction: "all" }); setOpen(false); refresh(); } finally { setBusy(false); } };
    const submitEdit = async (e) => { e.preventDefault(); setBusy(true); try { await api.patch(`/subjects/${edit.id}`, edit); setEdit(null); refresh(); } finally { setBusy(false); } };
    const del = async (sid) => { await api.delete(`/subjects/${sid}`); refresh(); };
    const dirLabel = (d) => d === "natural_sciences" ? "Shkenca Natyrore" : d === "informatics" ? "Teknologji Informative" : "Të gjitha drejtimet";
    const filtered = filterDir === "all" ? subjects : subjects.filter((s) => s.direction === filterDir || (filterDir === "informatics" && s.direction === "all") || (filterDir === "natural_sciences" && s.direction === "all"));
    const groupedShow = filterDir === "all" ? subjects : subjects.filter((s) => s.direction === filterDir || s.direction === "all");
    return (
        <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button onClick={() => setFilterDir("all")} className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterDir === "all" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`} data-testid="subjects-filter-all">Të gjitha ({subjects.length})</button>
                <button onClick={() => setFilterDir("informatics")} className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterDir === "informatics" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`} data-testid="subjects-filter-informatics">Teknologji Informative</button>
                <button onClick={() => setFilterDir("natural_sciences")} className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${filterDir === "natural_sciences" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200"}`} data-testid="subjects-filter-ns">Shkenca Natyrore</button>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button size="sm" className="ml-auto bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="admin-add-subject-btn"><Plus className="w-4 h-4" /> Shto Lëndë</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading">Shto Lëndë</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <Input placeholder="Emri i lëndës" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-add-subject-name" />
                            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="admin-add-subject-type">
                                <option value="general">E përgjithshme</option>
                                <option value="professional">Profesionale</option>
                            </select>
                            <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="admin-add-subject-direction">
                                <option value="all">Të gjitha drejtimet</option>
                                <option value="informatics">Teknologji Informative</option>
                                <option value="natural_sciences">Shkenca Natyrore</option>
                            </select>
                            <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-add-subject-submit">Ruaj</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {loading ? <div className="p-6 text-slate-500">Duke ngarkuar…</div> : groupedShow.length === 0 ? <div className="p-8 text-center text-slate-500">Asnjë lëndë.</div> : (
                    <div className="divide-y divide-slate-200">
                        {groupedShow.map((s) => (
                            <div key={s.id} className="flex items-center gap-3 px-4 sm:px-6 py-3" data-testid={`admin-subject-row-${s.id}`}>
                                <div className="w-10 h-10 rounded-xl bg-oxford/10 text-oxford flex items-center justify-center"><BookOpen className="w-5 h-5" /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-900 truncate">{s.name}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.type === "professional" ? "Profesionale" : "Përgjithshme"} • {dirLabel(s.direction)}</div>
                                </div>
                                <button onClick={() => setEdit({ ...s })} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" data-testid={`admin-edit-subject-${s.id}`}><Pencil className="w-4 h-4" /></button>
                                <ConfirmButton onConfirm={() => del(s.id)} testId={`admin-delete-subject-${s.id}`} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Edito Lëndën</DialogTitle></DialogHeader>
                    {edit && <form onSubmit={submitEdit} className="space-y-3">
                        <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} required data-testid="admin-edit-subject-name" />
                        <select value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                            <option value="general">E përgjithshme</option>
                            <option value="professional">Profesionale</option>
                        </select>
                        <select value={edit.direction || "all"} onChange={(e) => setEdit({ ...edit, direction: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                            <option value="all">Të gjitha drejtimet</option>
                            <option value="informatics">Teknologji Informative</option>
                            <option value="natural_sciences">Shkenca Natyrore</option>
                        </select>
                        <DialogFooter><Button type="submit" className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-edit-subject-submit">Ruaj</Button></DialogFooter>
                    </form>}
                </DialogContent>
            </Dialog>
        </>
    );
};

const ParentsTab = ({ parents, loading, refresh }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", name: "", password: "parent123" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [pwdDlg, setPwdDlg] = useState(null);
    const [pwdInput, setPwdInput] = useState("");
    const [editDlg, setEditDlg] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", email: "" });
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try { await api.post("/admin/parents", form); setForm({ email: "", name: "", password: "parent123" }); setOpen(false); refresh(); }
        catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    const submitPwd = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/admin/users/${pwdDlg.id}/password`, { new_password: pwdInput });
            setPwdDlg(null); setPwdInput("");
            alert("Fjalëkalimi u ndryshua.");
        } catch (e2) { alert("Gabim: " + (e2.response?.data?.detail || e2.message)); }
    };
    const submitEdit = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/admin/users/${editDlg.id}`, editForm);
            setEditDlg(null); refresh();
        } catch (e2) { alert("Gabim: " + (e2.response?.data?.detail || e2.message)); }
    };
    const deleteParent = async (p) => {
        if (!window.confirm(`Fshi prindin "${p.name}"? Nxënësit e tij do të shkëputen, por të dhënat e tyre mbeten.`)) return;
        try { await api.delete(`/admin/users/${p.id}`); refresh(); }
        catch (e2) { alert("Gabim: " + (e2.response?.data?.detail || e2.message)); }
    };
    return (
        <>
            <div className="flex justify-end mb-4">
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button size="sm" className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="admin-add-parent-btn"><UserPlus className="w-4 h-4" /> Krijo Login Prindi</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading">Krijo Login Prindi</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <Input placeholder="Emri i plotë" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="admin-add-parent-name" />
                            <Input type="email" placeholder="Emaili" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="admin-add-parent-email" />
                            <Input placeholder="Fjalëkalimi" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-add-parent-password" />
                            {err && <div className="text-xs text-terracotta">{err}</div>}
                            <DialogFooter><Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="admin-add-parent-submit">{busy ? "Duke krijuar…" : "Krijo"}</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {loading ? <div className="p-6 text-slate-500">Duke ngarkuar…</div> : parents.length === 0 ? <div className="p-8 text-center text-slate-500">Asnjë prind ende.</div> : (
                    <div className="divide-y divide-slate-200">
                        {parents.map((p) => (
                            <div key={p.id} className="flex items-center gap-2 px-4 sm:px-6 py-3" data-testid={`admin-parent-row-${p.id}`}>
                                <div className="w-10 h-10 rounded-xl bg-sage/15 text-sage flex items-center justify-center font-bold text-sm shrink-0">{p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-slate-900 truncate">{p.name}</div>
                                    <div className="text-xs text-slate-500 truncate">{p.email}</div>
                                </div>
                                <button onClick={() => { setEditDlg(p); setEditForm({ name: p.name, email: p.email }); }} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Edito" data-testid={`parent-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                                <button onClick={() => { setPwdDlg(p); setPwdInput(""); }} className="p-1.5 rounded-md text-seablue hover:bg-seablue/10" title="Ndrysho fjalëkalimin" data-testid={`parent-pwd-${p.id}`}><Key className="w-4 h-4" /></button>
                                <ConfirmButton onConfirm={() => deleteParent(p)} testId={`parent-delete-${p.id}`} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <Dialog open={!!pwdDlg} onOpenChange={(v) => !v && setPwdDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Ndrysho fjalëkalimin për {pwdDlg?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitPwd} className="space-y-3">
                        <Input type="password" placeholder="Fjalëkalimi i ri (min 6)" value={pwdInput} onChange={(e) => setPwdInput(e.target.value)} required minLength={6} data-testid="parent-pwd-input" />
                        <DialogFooter><Button type="submit" className="bg-oxford hover:bg-oxfordhover text-white" data-testid="parent-pwd-submit">Ndrysho</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            <Dialog open={!!editDlg} onOpenChange={(v) => !v && setEditDlg(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading">Edito prindin {editDlg?.name}</DialogTitle></DialogHeader>
                    <form onSubmit={submitEdit} className="space-y-3">
                        <Input placeholder="Emri" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="parent-edit-name" />
                        <Input type="email" placeholder="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required data-testid="parent-edit-email" />
                        <DialogFooter><Button type="submit" className="bg-oxford hover:bg-oxfordhover text-white" data-testid="parent-edit-submit">Ruaj</Button></DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};
